import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useToast,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Select,
  Badge,
  IconButton,
  Tooltip,
  Switch,
  Stack,
  Textarea,
  Divider,
} from '@chakra-ui/react';
import { AddIcon, EditIcon, DeleteIcon } from '@chakra-ui/icons';
import Card from 'components/card/Card.js';
import { getData, postData, putData, deleteData } from '../../../utils/httpClient';
import { useSearch } from '../../../contexts/SearchContext';

export default function LibraryMedia() {
  const [media, setMedia] = useState([]);
  const [mediaTypes, setMediaTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMediaTypes, setLoadingMediaTypes] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  const [selectedMedia, setSelectedMedia] = useState(null);
  
  const { searchQuery } = useSearch();

  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    author: '',
    code: '',
    publisher: '',
    edition: '',
    country: '',
    mediaTypeId: '',
  });

  const toast = useToast();
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  const fetchMedia = useCallback(async () => {
    try {
      setLoading(true);
      console.log('Fetching media...');
      const response = await getData('/library/media');
      console.log('Library media response:', response);
      
      // Asegurar que la respuesta sea un array
      if (Array.isArray(response)) {
        console.log('Setting media from array:', response.length, 'items');
        setMedia(response);
      } else if (response && Array.isArray(response.data)) {
        console.log('Setting media from response.data:', response.data.length, 'items');
        setMedia(response.data);
      } else if (response && Array.isArray(response.payload)) {
        console.log('Setting media from response.payload:', response.payload.length, 'items');
        setMedia(response.payload);
      } else {
        console.warn('Response is not an array:', response);
        setMedia([]);
      }
    } catch (error) {
      console.error('Error fetching media:', error);
      setMedia([]);
      toast({
        title: 'Error',
        description: 'Error al cargar los medios de la biblioteca',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const fetchMediaTypes = useCallback(async () => {
    try {
      setLoadingMediaTypes(true);
      
      // Intentar primero el endpoint principal
      let response;
      try {
        response = await getData('/library/media-types');
      } catch (error) {
        // Si falla, usar el endpoint simple como fallback
        response = await getData('/library/media-types-simple');
      }
      
      if (Array.isArray(response)) {
        setMediaTypes(response);
      } else if (response && Array.isArray(response.data)) {
        setMediaTypes(response.data);
      } else if (response && response.success && Array.isArray(response.data)) {
        setMediaTypes(response.data);
      } else {
        setMediaTypes([]);
      }
    } catch (error) {
      console.error('Error fetching media types:', error);
      setMediaTypes([]);
      toast({
        title: 'Error',
        description: 'Error al cargar los tipos de medios',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingMediaTypes(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchMedia();
    fetchMediaTypes();
  }, [fetchMedia, fetchMediaTypes]);

  const handleCreateMedia = () => {
    setModalMode('create');
    setSelectedMedia(null);
    setFormData({
      title: '',
      subtitle: '',
      author: '',
      code: '',
      publisher: '',
      edition: '',
      country: '',
      mediaTypeId: '',
    });
    setIsModalOpen(true);
  };

  const handleEditMedia = (mediaItem) => {
    setModalMode('edit');
    setSelectedMedia(mediaItem);
    setFormData({
      title: mediaItem.title || '',
      subtitle: mediaItem.subtitle || '',
      author: mediaItem.author || '',
      code: mediaItem.code || '',
      publisher: mediaItem.publisher || '',
      edition: mediaItem.edition || '',
      country: mediaItem.country || '',
      mediaTypeId: mediaItem.mediaTypeId || '',
    });
    setIsModalOpen(true);
  };

  const handleDeleteMedia = async (mediaId) => {
    if (window.confirm('¿Está seguro de que desea eliminar este medio?')) {
      try {
        await deleteData(`/library/media/${mediaId}`);
        toast({
          title: 'Éxito',
          description: 'Medio eliminado correctamente',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        fetchMedia();
      } catch (error) {
        toast({
          title: 'Error',
          description: 'Error al eliminar el medio',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    }
  };

  const handleSubmit = async () => {
    try {
      const mediaData = {
        ...formData,
        mediaTypeId: parseInt(formData.mediaTypeId),
      };

      if (modalMode === 'create') {
        await postData('/library/media', mediaData);
        toast({
          title: 'Éxito',
          description: 'Medio creado correctamente',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        await putData(`/library/media/${selectedMedia.id}`, mediaData);
        toast({
          title: 'Éxito',
          description: 'Medio actualizado correctamente',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }

      setIsModalOpen(false);
      fetchMedia();
    } catch (error) {
      toast({
        title: 'Error',
        description: `Error al ${modalMode === 'create' ? 'crear' : 'actualizar'} el medio`,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const filteredMedia = (Array.isArray(media) ? media : []).filter((item) =>
    searchQuery === '' ||
    item.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.author?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.publisher?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Card
        direction="column"
        w="100%"
        px="0px"
        overflowX={{ sm: 'scroll', lg: 'hidden' }}
      >
        <Flex px="25px" justify="space-between" mb="20px" align="center">
          <Text
            color={textColor}
            fontSize="22px"
            fontWeight="700"
            lineHeight="100%"
          >
            Gestión de Medios - Biblioteca
          </Text>
          <Button
            leftIcon={<AddIcon />}
            colorScheme="brand"
            variant="solid"
            onClick={handleCreateMedia}
          >
            Agregar Medio
          </Button>
        </Flex>

        {/* Tabla de medios */}
        <Table variant="simple" color="gray.500" mb="24px">
          <Thead>
            <Tr>
              <Th borderColor={borderColor}>Título</Th>
              <Th borderColor={borderColor}>Autor</Th>
              <Th borderColor={borderColor}>Código</Th>
              <Th borderColor={borderColor}>Editorial</Th>
              <Th borderColor={borderColor}>Edición</Th>
              <Th borderColor={borderColor}>País</Th>
              <Th borderColor={borderColor}>Tipo</Th>
              <Th borderColor={borderColor}>Acciones</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filteredMedia.map((item) => (
              <Tr 
                key={item.id}
                cursor="pointer"
                _hover={{ bg: hoverBg }}
              >
                <Td borderColor={borderColor}>
                  <Box>
                    <Text color={textColor} fontSize="sm" fontWeight="700">
                      {item.title}
                    </Text>
                    {item.subtitle && (
                      <Text color="gray.500" fontSize="xs">
                        {item.subtitle}
                      </Text>
                    )}
                  </Box>
                </Td>
                <Td borderColor={borderColor}>
                  <Text color={textColor} fontSize="sm">
                    {item.author}
                  </Text>
                </Td>
                <Td borderColor={borderColor}>
                  <Text color={textColor} fontSize="sm">
                    {item.code}
                  </Text>
                </Td>
                <Td borderColor={borderColor}>
                  <Text color={textColor} fontSize="sm">
                    {item.publisher}
                  </Text>
                </Td>
                <Td borderColor={borderColor}>
                  <Text color={textColor} fontSize="sm">
                    {item.edition}
                  </Text>
                </Td>
                <Td borderColor={borderColor}>
                  <Text color={textColor} fontSize="sm">
                    {item.country}
                  </Text>
                </Td>
                <Td borderColor={borderColor}>
                  <Badge colorScheme="blue">
                    {item.mediaType?.name || 'Sin tipo'}
                  </Badge>
                </Td>
                <Td borderColor={borderColor}>
                  <Flex gap="2">
                    <Tooltip label="Editar medio">
                      <IconButton
                        icon={<EditIcon />}
                        size="sm"
                        colorScheme="blue"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditMedia(item);
                        }}
                      />
                    </Tooltip>
                    <Tooltip label="Eliminar medio">
                      <IconButton
                        icon={<DeleteIcon />}
                        size="sm"
                        colorScheme="red"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteMedia(item.id);
                        }}
                      />
                    </Tooltip>
                  </Flex>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>

        {filteredMedia.length === 0 && !loading && (
          <Text textAlign="center" py="20px" color="gray.500">
            No se encontraron medios en la biblioteca
          </Text>
        )}
      </Card>

      {/* Modal para crear/editar medio */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} size="4xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {modalMode === 'create' ? 'Agregar Medio' : 'Editar Medio'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={4}>
              {/* Primera fila - Título y Subtítulo */}
              <Flex gap={4}>
                <FormControl isRequired flex="2">
                  <FormLabel>Título</FormLabel>
                  <Input
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    placeholder="Título del medio"
                  />
                </FormControl>

                <FormControl flex="1">
                  <FormLabel>Subtítulo</FormLabel>
                  <Input
                    value={formData.subtitle}
                    onChange={(e) => handleInputChange('subtitle', e.target.value)}
                    placeholder="Subtítulo (opcional)"
                  />
                </FormControl>
              </Flex>

              {/* Segunda fila - Autor y Tipo de Medio */}
              <Flex gap={4}>
                <FormControl isRequired>
                  <FormLabel>Autor</FormLabel>
                  <Input
                    value={formData.author}
                    onChange={(e) => handleInputChange('author', e.target.value)}
                    placeholder="Autor del medio"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Tipo de Medio</FormLabel>
                  <Select
                    value={formData.mediaTypeId}
                    onChange={(e) => handleInputChange('mediaTypeId', e.target.value)}
                    placeholder={loadingMediaTypes ? "Cargando tipos..." : "Seleccionar tipo"}
                    isDisabled={loadingMediaTypes}
                  >
                    {mediaTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </Select>
                  {!loadingMediaTypes && mediaTypes.length === 0 && (
                    <Text fontSize="xs" color="red.500" mt={1}>
                      No hay tipos de medios disponibles. Verifica la conexión a la base de datos.
                    </Text>
                  )}
                </FormControl>
              </Flex>

              {/* Tercera fila - Código y Editorial */}
              <Flex gap={4}>
                <FormControl isRequired>
                  <FormLabel>Código</FormLabel>
                  <Input
                    value={formData.code}
                    onChange={(e) => handleInputChange('code', e.target.value)}
                    placeholder="Código identificador"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Editorial</FormLabel>
                  <Input
                    value={formData.publisher}
                    onChange={(e) => handleInputChange('publisher', e.target.value)}
                    placeholder="Editorial"
                  />
                </FormControl>
              </Flex>

              {/* Cuarta fila - Edición y País */}
              <Flex gap={4}>
                <FormControl isRequired>
                  <FormLabel>Edición</FormLabel>
                  <Input
                    value={formData.edition}
                    onChange={(e) => handleInputChange('edition', e.target.value)}
                    placeholder="Número de edición"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>País</FormLabel>
                  <Input
                    value={formData.country}
                    onChange={(e) => handleInputChange('country', e.target.value)}
                    placeholder="País de publicación"
                  />
                </FormControl>
              </Flex>
            </Stack>
          </ModalBody>

          <ModalFooter>
            {!loadingMediaTypes && mediaTypes.length === 0 && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={fetchMediaTypes}
                mr={3}
              >
                Recargar Tipos
              </Button>
            )}
            <Button variant="ghost" mr={3} onClick={() => setIsModalOpen(false)}>
              Cancelar
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={handleSubmit}
              isDisabled={!formData.mediaTypeId || loadingMediaTypes}
            >
              {modalMode === 'create' ? 'Crear Medio' : 'Actualizar Medio'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
